import { server } from '@/constants';

import { onUnauthorized } from './unauthorized-handler';

export interface ApiClientOptions extends RequestInit {
  /** Custom base URL (defaults to server constant) */
  baseUrl?: string;
  /** Skip default headers (useful for FormData uploads where browser sets Content-Type) */
  skipDefaultHeaders?: boolean;
  /**
   * When true, the client will enforce that successful responses are JSON and
   * throw if the response is not JSON-parseable. When undefined or false,
   * non-JSON successful responses are returned as raw text.
   */
  expectJson?: boolean;
  /** Query parameters to append to the URL */
  query?: Record<string, string | number | boolean | undefined | null>;
}

export type EndpointMap = Record<string, string>;

export interface DownloadedFile {
  blob: Blob;
  /** Server-provided filename from content-disposition, or null when the header is absent. */
  filename: string | null;
}

/**
 * Reads the filename out of a content-disposition header.
 *
 * Prefers RFC 5987 `filename*` over the plain `filename` when both are present, since only the
 * former can carry non-ASCII characters.
 */
export function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) return null;

  const extended = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(header);
  if (extended?.[1]) {
    try {
      return decodeURIComponent(extended[1].trim());
    } catch {
      return extended[1].trim();
    }
  }

  const quoted = /filename\s*=\s*"((?:[^"\\]|\\.)*)"/.exec(header);
  if (quoted?.[1]) return quoted[1].replaceAll(/\\(.)/g, '$1');

  const bare = /filename\s*=\s*([^;]+)/.exec(header);
  return bare?.[1] ? bare[1].trim() : null;
}

export interface ApiError extends Error {
  status: number;
  statusText: string;
  data?: unknown;
}

/**
 * Recursively searches for error messages in nested objects
 */
function findErrorMessage(data: unknown, seen = new WeakSet<object>(), depth = 0, maxDepth = 10): string | null {
  // Avoid infinite recursion
  if (depth > maxDepth) {
    return null;
  }

  // Return primitives as-is
  if (data === null || data === undefined) {
    return null;
  }

  if (typeof data !== 'object') {
    if (typeof data === 'string') {
      return data;
    }
    return null;
  }

  // Handle circular references
  if (seen.has(data as object)) {
    return null;
  }
  seen.add(data as object);

  // Handle arrays - search first element
  if (Array.isArray(data)) {
    if (data.length > 0) {
      return findErrorMessage(data[0], seen, depth + 1, maxDepth);
    }
    return null;
  }

  // Handle objects - check ALL fields for common error message fields first,
  // before recursing into nested objects
  const errorObj = data as Record<string, unknown>;
  const searchOrder = ['message', 'detail', 'error', 'msg', 'err'];

  for (const field of searchOrder) {
    if (typeof errorObj[field] === 'string') {
      return errorObj[field] as string;
    }
  }

  // Check all fields that contain string values (but aren't the common error fields)
  // This catches cases like { session: "expired" }
  for (const [key, value] of Object.entries(errorObj)) {
    if (!searchOrder.includes(key) && typeof value === 'string') {
      return value;
    }
  }

  // If no string value found, recursively search all object/array values
  for (const value of Object.values(errorObj)) {
    if (typeof value === 'object' && value !== null) {
      const foundMessage = findErrorMessage(value, seen, depth + 1, maxDepth);
      if (foundMessage) {
        return foundMessage;
      }
    }
  }

  return null;
}

function buildFullUrl(url: string, baseUrl: string, query?: ApiClientOptions['query']): string {
  let fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;

  if (query) {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    }
    // Guard on the serialized result: an all-undefined query would otherwise append a bare `?`.
    const serialized = searchParams.toString();
    if (serialized) {
      fullUrl += (fullUrl.includes('?') ? '&' : '?') + serialized;
    }
  }

  return fullUrl;
}

/**
 * Reads an error body once and parses it as JSON only if it actually is JSON.
 *
 * `Response.json()` consumes the stream even when parsing fails, so a `json()`-then-`text()`
 * fallback throws "Body is unusable" and loses the real error.
 */
async function readErrorBody(response: Response): Promise<unknown> {
  let raw: string;
  try {
    raw = await response.text();
  } catch {
    return undefined;
  }

  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

/**
 * Creates an API error with additional context
 */
function createApiError(response: Response, data?: unknown): ApiError {
  // Extract error message from response data if available
  let errorMessage = response.statusText;

  if (data) {
    const foundMessage = findErrorMessage(data);
    if (foundMessage) {
      errorMessage = foundMessage;
    } else if (typeof data === 'string') {
      errorMessage = data;
    }
  }

  const error = new Error(errorMessage) as ApiError;
  error.status = response.status;
  error.statusText = response.statusText;
  error.data = data;
  return error;
}

/**
 * List of sensitive field names that should be redacted from logs
 */
const SENSITIVE_FIELDS = [
  'password',
  'token',
  'access_token',
  'refresh_token',
  'api_key',
  'apiKey',
  'secret',
  'authorization',
  'auth',
  'session',
  'sessionId',
  'csrf',
  'cookie',
  'credentials',
] as const;

/**
 * Sanitizes error data for logging by removing sensitive fields
 *
 * @param errorData - The error data to sanitize
 * @returns Sanitized error data safe for logging
 * This function recursively traverses error objects and redacts any fields
 * that might contain sensitive information like tokens, passwords, or session data.
 *
 * @example
 * ```typescript
 * const error = { message: 'Auth failed', token: 'secret123' };
 * const sanitized = sanitizeErrorForLogging(error);
 * // Returns: { message: 'Auth failed', token: '[REDACTED]' }
 * ```
 */
function sanitizeErrorForLogging(errorData: unknown, seen = new WeakSet()): unknown {
  // Return primitives as-is
  if (errorData === null || errorData === undefined) {
    return errorData;
  }

  if (typeof errorData !== 'object') {
    return errorData;
  }

  // Handle circular references
  if (seen.has(errorData as object)) {
    return '[Circular]';
  }
  seen.add(errorData as object);

  // Handle arrays
  if (Array.isArray(errorData)) {
    return errorData.map(item => sanitizeErrorForLogging(item, seen));
  }

  // Handle objects
  const sanitized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(errorData)) {
    const lowerKey = key.toLowerCase();

    // Check if this field should be redacted
    const isSensitive = SENSITIVE_FIELDS.some(field => lowerKey.includes(field.toLowerCase()));

    if (isSensitive) {
      sanitized[key] = '[REDACTED]';
    } else if (value && typeof value === 'object') {
      // Recursively sanitize nested objects
      sanitized[key] = sanitizeErrorForLogging(value, seen);
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
}

/**
 * Authenticated API client that checks auth status before requests
 * and automatically handles 401 responses
 */
export class UnauthorizedError extends Error {
  public isUnauthorized: boolean;
  constructor(message: string) {
    super(message);
    this.name = 'UnauthorizedError';
    this.isUnauthorized = true;
  }
}

class ApiClient {
  private static instance: ApiClient;
  private baseUrl: string = server;
  private endpointMap: EndpointMap = {};

  private constructor() {}

  static getInstance(): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient();
    }
    return ApiClient.instance;
  }

  /**
   * Configure the ApiClient global settings
   */
  configure(options: { baseUrl?: string; endpoints?: EndpointMap }) {
    if (options.baseUrl) this.baseUrl = options.baseUrl;
    if (options.endpoints) this.endpointMap = options.endpoints;
  }

  /**
   * Resolve a named endpoint from the endpoint map
   */
  getEndpoint(key: string): string | undefined {
    return this.endpointMap[key];
  }

  /**
   * Handle 401 Unauthorized responses
   */
  private handleUnauthorizedResponse(): void {
    // Defer the unauthorized handler to the next task so we don't trigger
    // synchronous navigation while in the middle of React's render/commit
    // lifecycle which can lead to nested updates.
    setTimeout(() => {
      try {
        onUnauthorized();
      } catch (err) {
        // Swallow to avoid interfering with calling context
        console.error('[ApiClient] onUnauthorized handler failed:', err);
      }
    }, 0);
  }

  /**
   * Makes an authenticated fetch request
   *
   * @param url - The URL to fetch (relative to baseUrl or absolute)
   * @param options - Fetch options with additional ApiClientOptions
   * @returns Promise resolving to the parsed JSON response, or null for 204 No Content
   *
   * @throws {ApiError} When the response is not ok
   *   * Note: This method returns `T | null` because 204 No Content responses return null.
   * If you need to ensure non-null responses, use the typed convenience methods or
   * check the result for null values.
   *
   * @example
   * ```typescript
   * // GET request
   * const data = await apiClient.fetch('/orgs/123/studies');
   *
   * // POST request
   * const result = await apiClient.fetch('/orgs/123/studies', {
   *   method: 'POST',
   *   body: JSON.stringify({ name: 'New Study' })
   * });
   *
   * // DELETE request (may return null for 204 No Content)
   * const result = await apiClient.fetch('/orgs/123/studies/456', {
   *   method: 'DELETE'
   * });
   * if (result === null) {
   *   console.log('Deleted successfully');
   * }
   * ```
   */
  async fetch<T = unknown>(url: string, options: ApiClientOptions = {}): Promise<T | null> {
    const { baseUrl = this.baseUrl, headers = {}, skipDefaultHeaders = false, ...fetchOptions } = options;

    const fullUrl = buildFullUrl(url, baseUrl, options.query);

    // Merge default headers with provided headers
    // Skip default headers for FormData uploads where browser needs to set Content-Type with boundary
    const isFormData = fetchOptions.body instanceof FormData;
    const finalHeaders: HeadersInit =
      skipDefaultHeaders || isFormData
        ? headers
        : {
            'Content-Type': 'application/json',
            ...headers,
          };

    // Log outgoing request details (with sensitive data redacted) only in DEV
    if (import.meta.env.DEV) {
      const sanitizedBody = fetchOptions.body
        ? sanitizeErrorForLogging(
            typeof fetchOptions.body === 'string'
              ? (() => {
                  try {
                    return JSON.parse(fetchOptions.body as string);
                  } catch {
                    return fetchOptions.body;
                  }
                })()
              : fetchOptions.body,
          )
        : undefined;

      console.info('[ApiClient] Request:', {
        url: fullUrl,
        method: fetchOptions.method || 'GET',
        headers: sanitizeErrorForLogging(finalHeaders),
        body: sanitizedBody,
      });
    }

    // Unlike `download()`, `fetchOptions` wins here — `get()`/`post()` supply `method` through it,
    // so a caller can also override credentials/mode. Only the auth-header defaults are pinned.
    const response = await fetch(fullUrl, {
      credentials: 'include',
      mode: 'cors',
      headers: finalHeaders,
      ...fetchOptions,
    });

    // Log response status and headers
    if (import.meta.env.DEV) {
      const headerEntries = Object.fromEntries(response.headers?.entries?.() ?? []);
      console.info('[ApiClient] Response:', {
        url: fullUrl,
        status: response.status,
        statusText: response.statusText,
        headers: headerEntries,
      });
    }

    // Handle 401 Unauthorized
    if (response.status === 401) {
      this.handleUnauthorizedResponse();
      throw createApiError(response);
    }

    // Handle other error responses
    if (!response.ok) {
      let errorData: unknown;
      try {
        errorData = await response.json();
      } catch {
        // Response body is not JSON or empty
        errorData = await response.text();
      }

      // Handle 403 Unauthorized
      if (response.status === 403) {
        throw createApiError(response, errorData);
      }

      const apiError = createApiError(response, errorData);

      // Log detailed error information (with sensitive data redacted)
      console.error('[ApiClient] API request failed:', {
        url: fullUrl,
        status: response.status,
        statusText: response.statusText,
        error: sanitizeErrorForLogging(errorData),
      });

      throw apiError;
    }

    // Parse successful response.
    try {
      const contentType = response.headers.get('content-type') ?? '';

      // If JSON, parse and return JSON
      if (contentType.includes('application/json')) {
        return await response.json();
      }

      // Return null for 204 No Content
      if (response.status === 204) {
        return null;
      }

      // Read text for non-JSON responses and decide behavior based on expectJson flag
      const text = await response.text();

      const expectJson = Boolean(options.expectJson);
      if (expectJson) {
        // Caller requested strict JSON; throw with helpful message
        console.error('[ApiClient] Expected JSON response but received non-JSON', {
          url: fullUrl,
          status: response.status,
          contentType,
          preview: text.slice(0, 200),
        });
        throw new Error(`Expected JSON response from ${fullUrl} but received content-type: ${contentType || 'none'}`);
      }

      if (import.meta.env.DEV) {
        console.warn('[ApiClient] Non-JSON successful response; returning raw text', {
          url: fullUrl,
          status: response.status,
          contentType,
          preview: text.slice(0, 200),
        });
      }

      // Return raw text as the generic result. Callers expecting a structured type should
      // either ensure the endpoint returns JSON or perform local parsing.
      return text as unknown as T;
    } catch (err) {
      console.error('[ApiClient] Failed to parse response:', err);
      throw err;
    }
  }

  /** Fetches a file attachment as a Blob: `fetch()` collapses non-JSON bodies to text and drops the
   *  response headers. Content-type is deliberately not asserted — the CSV exports are annotated
   *  `application/json` in the spec despite serving `text/csv` (`api.ts:6039`). */
  async download(url: string, options: Omit<ApiClientOptions, 'expectJson'> = {}): Promise<DownloadedFile> {
    const { baseUrl = this.baseUrl, headers = {}, query, ...fetchOptions } = options;

    const fullUrl = buildFullUrl(url, baseUrl, query);

    // `fetchOptions` spreads first so a caller cannot override method/credentials/mode — a download
    // is always a credentialed GET. Every other RequestInit key still passes through, and `fetch()`
    // deliberately keeps the opposite order — `get()`/`post()` set `method` through it.
    const response = await fetch(fullUrl, {
      ...fetchOptions,
      headers,
      credentials: 'include',
      mode: 'cors',
      method: 'GET',
    });

    if (response.status === 401) {
      this.handleUnauthorizedResponse();
      throw createApiError(response);
    }

    if (!response.ok) {
      const errorData = await readErrorBody(response);

      console.error('[ApiClient] Download failed:', {
        url: fullUrl,
        status: response.status,
        statusText: response.statusText,
        error: sanitizeErrorForLogging(errorData),
      });

      throw createApiError(response, errorData);
    }

    return {
      blob: await response.blob(),
      filename: parseContentDispositionFilename(response.headers.get('content-disposition')),
    };
  }

  /**
   * Convenience method for GET requests
   *   * Note: Returns T | null because 204 No Content responses return null.
   */
  async get<T = unknown>(url: string, options?: Omit<ApiClientOptions, 'method' | 'body'>): Promise<T | null> {
    return this.fetch<T>(url, { ...options, method: 'GET' });
  }

  /**
   * Convenience method for POST requests
   *   * Note: Returns T | null because 204 No Content responses return null.
   */
  async post<T = unknown>(
    url: string,
    body?: unknown,
    options?: Omit<ApiClientOptions, 'method' | 'body'>,
  ): Promise<T | null> {
    return this.fetch<T>(url, {
      ...options,
      method: 'POST',
      body: body instanceof FormData ? body : body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * Convenience method for PUT requests
   *   * Note: Returns T | null because 204 No Content responses return null.
   */
  async put<T = unknown>(
    url: string,
    body?: unknown,
    options?: Omit<ApiClientOptions, 'method' | 'body'>,
  ): Promise<T | null> {
    return this.fetch<T>(url, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * Convenience method for PATCH requests
   *   * Note: Returns T | null because 204 No Content responses return null.
   */
  async patch<T = unknown>(
    url: string,
    body?: unknown,
    options?: Omit<ApiClientOptions, 'method' | 'body'>,
  ): Promise<T | null> {
    return this.fetch<T>(url, {
      ...options,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * Convenience method for DELETE requests
   *   * DELETE requests typically return 204 No Content, so this will usually return null.
   * If you need to handle the null case explicitly, check the return value:
   *
   * @example
   * ```typescript
   * const result = await apiClient.delete('/orgs/123/studies/456');
   * if (result === null) {
   *   console.log('Deleted successfully (204 No Content)');
   * }
   * ```
   */
  /**
   * Convenience method for DELETE requests
   *   * DELETE requests typically return 204 No Content, so this will usually return null.
   * If you need to handle the null case explicitly, check the return value:
   *
   * @example
   * ```typescript
   * const result = await apiClient.delete('/orgs/123/studies/456');
   * if (result === null) {
   *   console.log('Deleted successfully (204 No Content)');
   * }
   * ```
   *
   * @param url - The URL to send the DELETE request to.
   * @param bodyOrOptions - If an object (but not a RequestInit, Headers, FormData, or URLSearchParams), treated as the request body.
   *                        If an ApiClientOptions object, treated as options.
   *                        If a primitive or unexpected type, an error will be thrown.
   * @param options - Additional options to merge in if bodyOrOptions is a body.
   */
  async delete<T = unknown>(
    url: string,
    bodyOrOptions?: unknown,
    options?: Omit<ApiClientOptions, 'method' | 'body'>,
  ): Promise<T | null> {
    let fetchOptions: ApiClientOptions = { method: 'DELETE' };

    if (bodyOrOptions === undefined) {
      // No body or options
      // fetchOptions is just { method: 'DELETE' }
    } else if (
      typeof bodyOrOptions === 'object' &&
      bodyOrOptions !== null &&
      !(bodyOrOptions instanceof Headers) &&
      !(bodyOrOptions instanceof URLSearchParams) &&
      !(bodyOrOptions instanceof FormData)
    ) {
      // Could be body or options, check for known ApiClientOptions keys
      const maybeOptions = bodyOrOptions as Partial<ApiClientOptions>;
      const isProbablyOptions =
        'baseUrl' in maybeOptions ||
        'skipDefaultHeaders' in maybeOptions ||
        'query' in maybeOptions ||
        'headers' in maybeOptions ||
        'credentials' in maybeOptions ||
        'mode' in maybeOptions ||
        'cache' in maybeOptions ||
        'redirect' in maybeOptions ||
        'referrer' in maybeOptions ||
        'integrity' in maybeOptions ||
        'keepalive' in maybeOptions ||
        'signal' in maybeOptions;

      if (isProbablyOptions) {
        fetchOptions = {
          ...fetchOptions,
          ...(bodyOrOptions as ApiClientOptions),
        };
      } else {
        // Treat as body
        fetchOptions.body = JSON.stringify(bodyOrOptions);
        if (options) {
          fetchOptions = { ...fetchOptions, ...options };
        }
      }
    } else if (
      typeof bodyOrOptions === 'string' ||
      typeof bodyOrOptions === 'number' ||
      typeof bodyOrOptions === 'boolean'
    ) {
      // Primitives are not valid for body or options
      throw new TypeError(
        `apiClient.delete: bodyOrOptions must be an object (body or ApiClientOptions), not a primitive (${typeof bodyOrOptions})`,
      );
    } else {
      // Fallback: treat as options if possible, otherwise throw
      if (bodyOrOptions) {
        fetchOptions = {
          ...fetchOptions,
          ...(bodyOrOptions as ApiClientOptions),
        };
      }
    }

    return this.fetch<T>(url, fetchOptions);
  }
}

// Export singleton instance
export const apiClient = ApiClient.getInstance();

// Export type for the client
export type { ApiClient };
